import React, { useEffect, useMemo, useState } from 'react';
import { App, Badge, Button, Drawer, Empty, Popover, Skeleton, Space, Tag, Typography } from 'antd';
import {
  BellOutlined,
  BookOutlined,
  CheckOutlined,
  CommentOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import useNotificationStore from '../../store/notificationStore';
import './Notifications.css';

const { Text, Title } = Typography;

export const NOTIFICATION_META = {
  MANUSCRIPT: { label: 'Manuscript', color: '#1677ff', surface: '#e6f4ff', icon: FileTextOutlined },
  REVIEW: { label: 'Review', color: '#6d28d9', surface: '#f5f3ff', icon: BookOutlined },
  PUBLICATION: { label: 'Publication', color: '#047857', surface: '#ecfdf5', icon: BookOutlined },
  PAYMENT: { label: 'Payment', color: '#b45309', surface: '#fffbeb', icon: CreditCardOutlined },
  DISCUSSION: { label: 'Discussion', color: '#0e7490', surface: '#ecfeff', icon: CommentOutlined },
  ACCOUNT: { label: 'Account', color: '#be123c', surface: '#fff1f2', icon: SafetyCertificateOutlined },
  SYSTEM: { label: 'System', color: '#475569', surface: '#f1f5f9', icon: SettingOutlined },
};

const relativeTime = (value) => {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch (_) {
    return 'Recently';
  }
};

export const NotificationItem = ({ item, onOpen, showCategory = true, actions = null }) => {
  const meta = NOTIFICATION_META[item.category] || NOTIFICATION_META.SYSTEM;
  const Icon = meta.icon;
  return (
    <div className={`notification-item ${item.read ? '' : 'notification-item-unread'}`}>
      <button type="button" className="notification-item-main" onClick={() => onOpen(item)}>
        <span className="notification-item-icon" style={{ color: meta.color, background: meta.surface }}>
          <Icon />
        </span>
        <span className="notification-item-copy">
          <span className="notification-item-title-row">
            <strong>{item.title}</strong>
            {!item.read && <span className="notification-unread-dot" aria-label="Unread" />}
          </span>
          <span className="notification-item-message">{item.message}</span>
          <span className="notification-item-meta">
            {showCategory && <Tag color={meta.color}>{meta.label}</Tag>}
            <span>{relativeTime(item.createdAt)}</span>
          </span>
        </span>
      </button>
      {actions}
    </div>
  );
};

const RecentNotifications = ({ onClose }) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const recentItems = useNotificationStore(state => state.recentItems);
  const unreadCount = useNotificationStore(state => state.unreadCount);
  const loading = useNotificationStore(state => state.loadingRecent);
  const error = useNotificationStore(state => state.error);
  const setReadState = useNotificationStore(state => state.setReadState);
  const markAllRead = useNotificationStore(state => state.markAllRead);
  const fetchRecent = useNotificationStore(state => state.fetchRecent);

  const openNotification = async (item) => {
    if (!item.read) await setReadState(item.id, true).catch(() => message.error('Could not update notification'));
    if (item.actionUrl?.startsWith('/') && !item.actionUrl.startsWith('//')) navigate(item.actionUrl);
    onClose?.();
  };

  return (
    <div className="notification-popover-content">
      <div className="notification-popover-header">
        <div>
          <Title level={5}>Notifications</Title>
          <Text type="secondary">{unreadCount ? `${unreadCount} unread` : 'You are all caught up'}</Text>
        </div>
        <Button type="text" size="small" icon={<CheckOutlined />} disabled={!unreadCount} onClick={() => markAllRead().catch(() => message.error('Could not mark notifications as read'))}>
          Mark all read
        </Button>
      </div>
      <div className="notification-recent-list">
        {loading && !recentItems.length ? <Skeleton active paragraph={{ rows: 4 }} /> : null}
        {!loading && error && !recentItems.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error}>
            <Button onClick={() => fetchRecent().catch(() => {})}>Retry</Button>
          </Empty>
        ) : null}
        {!loading && !error && !recentItems.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications yet" /> : null}
        {recentItems.map(item => <NotificationItem key={item.id} item={item} onOpen={openNotification} showCategory={false} />)}
      </div>
      <Button type="link" block className="notification-view-all" onClick={() => { navigate('/notifications'); onClose?.(); }}>
        View all notifications
      </Button>
    </div>
  );
};

const NotificationCenter = ({ mobile = false }) => {
  const [open, setOpen] = useState(false);
  const unreadCount = useNotificationStore(state => state.unreadCount);
  const fetchRecent = useNotificationStore(state => state.fetchRecent);
  const connectStream = useNotificationStore(state => state.connectStream);
  const disconnectStream = useNotificationStore(state => state.disconnectStream);
  const lastIncoming = useNotificationStore(state => state.lastIncoming);
  const clearLastIncoming = useNotificationStore(state => state.clearLastIncoming);
  const { notification } = App.useApp();

  useEffect(() => {
    fetchRecent().catch(() => {});
    connectStream();
    const refresh = () => fetchRecent().catch(() => {});
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      disconnectStream();
    };
  }, [connectStream, disconnectStream, fetchRecent]);

  useEffect(() => {
    if (!lastIncoming) return;
    notification.open({
      message: lastIncoming.title,
      description: lastIncoming.message,
      placement: 'topRight',
      duration: 5,
    });
    clearLastIncoming();
  }, [clearLastIncoming, lastIncoming, notification]);

  const bell = useMemo(() => (
    <Badge count={unreadCount} overflowCount={99} size="small">
      <Button
        type="text"
        icon={<BellOutlined />}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        className="notification-bell-button"
      />
    </Badge>
  ), [unreadCount]);

  const handleOpen = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) fetchRecent().catch(() => {});
  };

  if (mobile) {
    return (
      <>
        <span onClick={() => handleOpen(true)}>{bell}</span>
        <Drawer title="Notification centre" open={open} onClose={() => setOpen(false)} width="100%" className="notification-drawer">
          <RecentNotifications onClose={() => setOpen(false)} />
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpen}
      content={<RecentNotifications onClose={() => setOpen(false)} />}
      overlayClassName="notification-popover"
    >
      {bell}
    </Popover>
  );
};

export default NotificationCenter;
